import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Title } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PageHeader from "./PageHeader";
import {
  ACTIVE_RIDE_STATUSES,
  acceptRide,
  assignRide,
  formatDateTime,
  loadSupplierDrivers,
  loadSupplierRides,
  rejectRide,
  statusLabel,
} from "./supplierApi";

const configs = {
  new: {
    title: "New Bookings",
    subtitle: "Booking offers shared with your supplier account and waiting for your response.",
    statuses: ["shared"],
    icon: <InboxIcon />,
  },
  accepted: {
    title: "Accepted Bookings",
    subtitle: "Accepted bookings waiting for one of your chauffeurs to be assigned.",
    statuses: ["accepted"],
    icon: <TaskAltIcon />,
  },
  active: {
    title: "Active Rides",
    subtitle: "Your assigned and in-progress rides. Reassignment is available only before the chauffeur starts travelling.",
    statuses: ACTIVE_RIDE_STATUSES,
    icon: <DirectionsCarIcon />,
  },
  completed: {
    title: "Completed Rides",
    subtitle: "Completed trips linked to your supplier account.",
    statuses: ["completed"],
    icon: <HistoryIcon />,
  },
};

function StatusChip({ status }) {
  let color = "default";
  if (status === "shared") color = "secondary";
  if (status === "accepted") color = "success";
  if (ACTIVE_RIDE_STATUSES.includes(status)) color = "primary";
  return <Chip label={statusLabel(status)} size="small" color={color} variant={color === "default" ? "outlined" : "filled"} />;
}

function shortText(value, length = 32) {
  const text = String(value || "—");
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

export default function SupplierBookingsPage({ mode }) {
  const config = configs[mode] || configs.active;
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [assignRideRecord, setAssignRideRecord] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await loadSupplierRides(config.statuses);
      setRides(data);
      setSelected([]);
    } catch (e) {
      setError(e?.message || "Unable to load bookings.");
    } finally {
      setLoading(false);
    }
  }, [config.statuses]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rides;
    return rides.filter((r) => [
      r.tripId,
      r.passengerName,
      r.passengerPhone,
      r.pickupLocation,
      r.dropLocation,
      r.flightNumber,
      r.driverName,
      r.car?.plateNumber,
    ].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rides, search]);

  const openAssign = async (record) => {
    setError("");
    try {
      const ownDrivers = await loadSupplierDrivers();
      setDrivers(ownDrivers);
      setSelectedDriverId(record?.driverId || "");
      setAssignRideRecord(record);
    } catch (e) {
      setError(e?.message || "Unable to load your drivers.");
    }
  };

  const handleAssign = async () => {
    const driver = drivers.find((d) => d.id === selectedDriverId);
    if (!assignRideRecord || !driver) return;
    setBusyId(assignRideRecord.id);
    try {
      await assignRide(assignRideRecord.id, driver);
      setAssignRideRecord(null);
      setSelectedDriverId("");
      await refresh();
    } catch (e) {
      setError(e?.message || "Driver assignment failed.");
    } finally {
      setBusyId("");
    }
  };

  const doAccept = async (record) => {
    setBusyId(record.id);
    try {
      await acceptRide(record.id);
      await refresh();
    } catch (e) {
      setError(e?.message || "Unable to accept booking.");
    } finally {
      setBusyId("");
    }
  };

  const doReject = async (record) => {
    setBusyId(record.id);
    try {
      await rejectRide(record.id);
      await refresh();
    } catch (e) {
      setError(e?.message || "Unable to reject booking.");
    } finally {
      setBusyId("");
    }
  };

  const bulkAccept = async () => {
    if (!selected.length) return;
    setBusyId("bulk");
    try {
      await Promise.all(selected.map((id) => acceptRide(id)));
      await refresh();
    } catch (e) {
      setError(e?.message || "Bulk accept failed.");
    } finally {
      setBusyId("");
    }
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.includes(r.id));

  return (
    <Box>
      <Title title={config.title} />
      <PageHeader icon={config.icon} title={config.title} subtitle={config.subtitle} onRefresh={refresh} refreshing={loading} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between" sx={{ mb: 1.6 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search trip, guest, location, flight or driver"
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
            sx={{ width: { xs: "100%", md: 430 } }}
          />
          {mode === "new" && selected.length > 0 && (
            <Button variant="contained" color="success" startIcon={<DoneAllIcon />} onClick={bulkAccept} disabled={busyId === "bulk"}>
              Accept Selected ({selected.length})
            </Button>
          )}
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Box sx={{ width: 70, height: 70, borderRadius: 3, bgcolor: "rgba(211,47,47,0.06)", display: "grid", placeItems: "center", mx: "auto", mb: 1.5, color: "primary.main" }}>
              {config.icon}
            </Box>
            <Typography sx={{ fontWeight: 800 }}>Nothing to show here</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {mode === "new" ? "There are no new booking offers at the moment." : `No ${config.title.toLowerCase()} found.`}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  {mode === "new" && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        onChange={(e) => {
                          if (e.target.checked) setSelected(Array.from(new Set([...selected, ...filtered.map((r) => r.id)])));
                          else setSelected(selected.filter((id) => !filtered.some((r) => r.id === id)));
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Trip</TableCell>
                  <TableCell>Passenger</TableCell>
                  <TableCell>Pickup</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Flight / Type</TableCell>
                  <TableCell>Guests</TableCell>
                  {(mode === "active" || mode === "completed") && <TableCell>Driver & Vehicle</TableCell>}
                  <TableCell>Status</TableCell>
                  {mode !== "completed" && <TableCell align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((ride) => (
                  <TableRow key={ride.id} hover>
                    {mode === "new" && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(ride.id)}
                          onChange={(e) => setSelected((prev) => e.target.checked ? [...prev, ride.id] : prev.filter((id) => id !== ride.id))}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{ride.tripId || ride.id}</Typography>
                      {ride.clientReference && <Typography variant="caption" color="text.secondary">Ref: {ride.clientReference}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{ride.passengerName || "Guest"}</Typography>
                      <Typography variant="caption" color="text.secondary">{ride.passengerPhone || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(ride.pickupTimestamp)}</TableCell>
                    <TableCell>
                      <Tooltip title={`${ride.pickupLocation || ""} → ${ride.dropLocation || ""}`}>
                        <Box>
                          <Typography sx={{ fontSize: 13 }}>{shortText(ride.pickupLocation, 28)}</Typography>
                          <Typography variant="caption" color="text.secondary">→ {shortText(ride.dropLocation, 28)}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{ride.flightNumber || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ride.bookingType || "Transfer"}{ride.bookingType === "hourly" && ride.numberOfHours ? ` · ${ride.numberOfHours}h` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{ride.numberOfGuests || 0} guest(s)</Typography>
                      <Typography variant="caption" color="text.secondary">{ride.numberOfLuggage || 0} bag(s)</Typography>
                    </TableCell>
                    {(mode === "active" || mode === "completed") && (
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{ride.driverName || "Not assigned"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ride.car ? `${ride.car.brand || ""} ${ride.car.model || ""} · ${ride.car.plateNumber || ""}`.trim() : "—"}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell><StatusChip status={ride.status} /></TableCell>
                    {mode !== "completed" && (
                      <TableCell align="right">
                        {mode === "new" && (
                          <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                            <Button size="small" variant="contained" color="success" onClick={() => doAccept(ride)} disabled={busyId === ride.id}>Accept</Button>
                            <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => doReject(ride)} disabled={busyId === ride.id}>Reject</Button>
                          </Stack>
                        )}
                        {mode === "accepted" && (
                          <Button size="small" variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={() => openAssign(ride)}>Assign Driver</Button>
                        )}
                        {mode === "active" && ride.status === "assigned" && (
                          <Button size="small" variant="outlined" onClick={() => openAssign(ride)}>Reassign</Button>
                        )}
                        {mode === "active" && ride.status !== "assigned" && (
                          <Typography variant="caption" color="text.secondary">In progress</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={Boolean(assignRideRecord)} onClose={() => setAssignRideRecord(null)} fullWidth maxWidth="sm">
        <DialogTitle>{assignRideRecord?.status === "assigned" ? "Reassign Chauffeur" : "Assign Chauffeur"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only chauffeurs registered under your supplier account are available here.
          </Typography>
          {drivers.length === 0 ? (
            <Alert severity="info">No supplier drivers found. Add a driver from My Drivers & Cars first.</Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="driver-select-label">Driver</InputLabel>
              <Select labelId="driver-select-label" label="Driver" value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}>
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName} — {driver.car?.brand || "Vehicle"} {driver.car?.model || ""} ({driver.car?.plateNumber || "No plate"})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignRideRecord(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!selectedDriverId || Boolean(busyId)}>Confirm Assignment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
